import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  PREG_ACCENT,
  recordCardTint,
  recordCardTintBorder
} from '../clinical-notes-section/recordCardStyles';
import { LAUNCHER_HEIGHT, SYMBOL_SLOT } from './tileMetrics';

/**
 * A full-width row that OPENS A FORM rather than adding a node. Used three times:
 * Add fetus, Conceived using a donor, Is a gamete donor.
 *
 * Separate from RelativeTile because it is a different class of control, and the
 * differences are behavioural rather than cosmetic: it has no count, it never
 * joins a batch, it produces no confirmation and it cannot be undone by this
 * panel. Wiring one into the panel's undo would ship a button that does not work
 * — a pregnancy is a Pregnancy record written through /proband/pregnancy, and a
 * donor link is a gameteDonors entry persisted with the pedigree config, so
 * neither is in the member array the batch undo removes from.
 *
 * It says so three ways rather than one: the reproductive accent (PREG_ACCENT, the
 * same pink the pregnancy tiles and prenatal chips use, so a clinician already
 * reads it as reproductive data), the trailing ellipsis in the label, and a
 * chevron rather than a symbol on the right.
 *
 * @param {Object}   props
 * @param {React.ReactNode} props.symbol
 * @param {string}   props.label            Short form, sentence case, trailing …
 * @param {string}   props.accessibleName   Says that it opens something, and
 *                                          carries the long-form sentence.
 * @param {Function} props.onOpen
 * @param {boolean}  [props.disabled]
 * @param {string}   [props.disabledReason] Tooltip + appended to the name.
 */
const ReproLauncher = ({
  symbol,
  label,
  accessibleName,
  onOpen,
  disabled = false,
  disabledReason = ''
}) => {
  const name = disabledReason
    ? `${accessibleName || label}, ${disabledReason}`
    : accessibleName || label;

  const row = (
    <Box
      component="button"
      type="button"
      aria-label={name}
      aria-disabled={disabled ? 'true' : undefined}
      disabled={disabled}
      onClick={() => !disabled && onOpen()}
      title={accessibleName || label}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: LAUNCHER_HEIGHT,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        pl: 0,
        pr: 1.25,
        py: 0.75,
        font: 'inherit',
        textAlign: 'left',
        cursor: disabled ? 'default' : 'pointer',
        borderRadius: 1.5,
        border: 1,
        overflow: 'hidden',
        borderColor: recordCardTintBorder(PREG_ACCENT),
        bgcolor: disabled ? 'background.paper' : recordCardTint(PREG_ACCENT),
        color: disabled ? 'text.secondary' : 'text.primary',
        transition: 'background-color 120ms ease, border-color 120ms ease',
        '&:hover': disabled
          ? {}
          : {
              borderColor: PREG_ACCENT,
              bgcolor: recordCardTint(PREG_ACCENT, 0.14)
            },
        '&:focus-visible': {
          outline: 2,
          outlineColor: PREG_ACCENT,
          outlineOffset: 2
        }
      }}
    >
      {/* Accent bar, the same device the pregnancy record tiles use to mark
          reproductive data. It also gives the glyph a consistent left edge, so the
          three rows line up whatever their symbol is. */}
      <Box
        aria-hidden="true"
        sx={{
          alignSelf: 'stretch',
          width: 4,
          flexShrink: 0,
          bgcolor: disabled ? 'divider' : PREG_ACCENT
        }}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: SYMBOL_SLOT,
          width: SYMBOL_SLOT,
          color: disabled ? 'text.disabled' : PREG_ACCENT,
          flexShrink: 0
        }}
      >
        {symbol}
      </Box>
      <Typography
        component="span"
        sx={{ flex: 1, fontSize: 13, fontWeight: 600, lineHeight: 1.25 }}
      >
        {label}
      </Typography>
      <ChevronRightIcon
        aria-hidden="true"
        sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
      />
    </Box>
  );

  return disabledReason ? (
    <Tooltip title={disabledReason} placement="top">
      <span style={{ display: 'block', width: '100%', height: '100%' }}>
        {row}
      </span>
    </Tooltip>
  ) : (
    row
  );
};

export default ReproLauncher;
